import { createSlice, createAction } from '@reduxjs/toolkit';
import { globalReset } from '@/store/actions';
import {
    SpeakingSession,
    SpeakingAttempt,
    StartSessionRequest,
    AudioUploadRequest,
    PaginatedResponse,
} from '@/services/speaking/types';
import { first } from 'lodash';

// === SESSIONS ACTIONS ===
export const fetchSessionsRequest = createAction<{ page?: number; pageSize?: number }>('sessions/fetchSessionsRequest');
export const fetchSessionsSuccess = createAction<PaginatedResponse<SpeakingSession>>('sessions/fetchSessionsSuccess');
export const fetchSessionsFailure = createAction<string>('sessions/fetchSessionsFailure');

export const fetchSessionByIdRequest = createAction<string>('sessions/fetchSessionByIdRequest');
export const fetchSessionByIdSuccess = createAction<SpeakingSession>('sessions/fetchSessionByIdSuccess');
export const fetchSessionByIdFailure = createAction<string>('sessions/fetchSessionByIdFailure');

export const startSessionRequest = createAction<StartSessionRequest>('sessions/startSessionRequest');
export const startSessionSuccess = createAction<SpeakingSession>('sessions/startSessionSuccess');
export const startSessionFailure = createAction<string>('sessions/startSessionFailure');

export const endSessionRequest = createAction<string>('sessions/endSessionRequest');
export const endSessionSuccess = createAction<SpeakingSession>('sessions/endSessionSuccess');
export const endSessionFailure = createAction<string>('sessions/endSessionFailure');

export const completeSessionRequest = createAction<{ sessionId: string; completionData?: any }>(
    'sessions/completeSessionRequest',
);
export const completeSessionSuccess = createAction<any>('sessions/completeSessionSuccess');
export const completeSessionFailure = createAction<string>('sessions/completeSessionFailure');

export const uploadAudioRequest = createAction<AudioUploadRequest>('sessions/uploadAudioRequest');
export const uploadAudioSuccess = createAction<SpeakingAttempt>('sessions/uploadAudioSuccess');
export const uploadAudioFailure = createAction<string>('sessions/uploadAudioFailure');

export const setCurrentSession = createAction<SpeakingSession | null>('sessions/setCurrentSession');
export const resetSessionState = createAction('sessions/resetSessionState');

// === STATE INTERFACE ===
export interface SessionsState {
    sessions: SpeakingSession[];
    currentSession: SpeakingSession | null;
    currentAttempt: SpeakingAttempt | null;

    // Pagination
    totalSessions: number;
    currentPage: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;

    // Recording state
    isRecording: boolean;
    recordingDuration: number;
    audioBlob: Blob | null;

    // Loading States
    isLoadingSessions: boolean;
    isLoadingSession: boolean;
    isStartingSession: boolean;
    isEndingSession: boolean;
    isCompletingSession: boolean;
    isUploadingAudio: boolean;

    // Error States
    sessionsError: string | null;
    sessionError: string | null;
    startSessionError: string | null;
    endSessionError: string | null;
    completeSessionError: string | null;
    uploadError: string | null;
}

const initialState: SessionsState = {
    sessions: [],
    currentSession: null,
    currentAttempt: null,

    totalSessions: 0,
    currentPage: 1,
    hasNextPage: false,
    hasPreviousPage: false,

    isRecording: false,
    recordingDuration: 0,
    audioBlob: null,

    isLoadingSessions: false,
    isLoadingSession: false,
    isStartingSession: false,
    isEndingSession: false,
    isCompletingSession: false,
    isUploadingAudio: false,

    sessionsError: null,
    sessionError: null,
    startSessionError: null,
    endSessionError: null,
    completeSessionError: null,
    uploadError: null,
};

const sessionsSlice = createSlice({
    name: 'sessions',
    initialState,
    reducers: {
        setRecordingState: (state, action) => {
            state.isRecording = action.payload.isRecording;
            state.recordingDuration = action.payload.duration || 0;
        },
        setAudioBlob: (state, action) => {
            state.audioBlob = action.payload;
        },
        incrementRecordingDuration: (state) => {
            state.recordingDuration += 1;
        },
        clearUploadError: (state) => {
            state.uploadError = null;
        },
        resetState: (state) => {
            state.currentAttempt = null
            state.currentSession = null
            // A refusal (e.g. no attempts left) belongs to the activity that got it, not the next one
            state.startSessionError = null
            state.completeSessionError = null
            state.uploadError = null
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(globalReset, () => initialState)
            .addCase(resetSessionState, () => initialState)

            // === FETCH SESSIONS ===
            .addCase(fetchSessionsRequest, (state, action) => {
                state.isLoadingSessions = true;
                state.sessionsError = null;
                state.currentPage = action.payload.page || 1;
            })
            .addCase(fetchSessionsSuccess, (state, action) => {
                state.isLoadingSessions = false;
                state.sessions = action.payload.results;
                state.totalSessions = action.payload.count;
                state.hasNextPage = !!action.payload.next;
                state.hasPreviousPage = !!action.payload.previous;
            })
            .addCase(fetchSessionsFailure, (state, action) => {
                state.isLoadingSessions = false;
                state.sessionsError = action.payload;
            })

            // === FETCH SESSION BY ID ===
            .addCase(fetchSessionByIdRequest, (state) => {
                state.isLoadingSession = true;
                state.sessionError = null;
            })
            .addCase(fetchSessionByIdSuccess, (state, action) => {
                state.isLoadingSession = false;
                state.currentSession = action.payload;
            })
            .addCase(fetchSessionByIdFailure, (state, action) => {
                state.isLoadingSession = false;
                state.sessionError = action.payload;
            })

            // === START SESSION ===
            .addCase(startSessionRequest, (state) => {
                state.isStartingSession = true;
                state.startSessionError = null;
            })
            .addCase(startSessionSuccess, (state, action) => {
                state.isStartingSession = false;
                state.currentSession = action.payload;
                // Add to sessions list if not already there
                const existingIndex = state.sessions.findIndex((s) => s.id === action.payload.id);
                if (existingIndex === -1) {
                    state.sessions.unshift(action.payload);
                }
            })
            .addCase(startSessionFailure, (state, action) => {
                state.isStartingSession = false;
                state.startSessionError = action.payload;
            })

            // === END SESSION ===
            .addCase(endSessionRequest, (state) => {
                state.isEndingSession = true;
                state.endSessionError = null;
            })
            .addCase(endSessionSuccess, (state, action) => {
                state.isEndingSession = false;
                state.currentSession = action.payload;
                // Update session in sessions list
                const sessionIndex = state.sessions.findIndex((s) => s.id === action.payload.id);
                if (sessionIndex !== -1) {
                    state.sessions[sessionIndex] = action.payload;
                }
            })
            .addCase(endSessionFailure, (state, action) => {
                state.isEndingSession = false;
                state.endSessionError = action.payload;
            })

            // === COMPLETE SESSION ===
            .addCase(completeSessionRequest, (state) => {
                state.isCompletingSession = true;
                state.completeSessionError = null;
            })
            .addCase(completeSessionSuccess, (state, action) => {
                state.isCompletingSession = false;
                // Update current session with completion data
                if (state.currentSession && action.payload.session_id === state.currentSession.id) {
                    state.currentSession.base_session = {
                        ...state.currentSession.base_session,
                        status: 'completed',
                        completed_at: action.payload.completed_at,
                    };
                }
                // Update session in sessions list
                const sessionIndex = state.sessions.findIndex((s) => s.id === action.payload.session_id);
                if (sessionIndex !== -1) {
                    state.sessions[sessionIndex] = {
                        ...state.sessions[sessionIndex],
                        base_session: {
                            ...state.sessions[sessionIndex].base_session,
                            status: 'completed',
                            completed_at: action.payload.completed_at,
                        },
                    };
                }
                state.currentAttempt = first(action.payload.attempts);
            })
            .addCase(completeSessionFailure, (state, action) => {
                state.isCompletingSession = false;
                state.completeSessionError = action.payload;
            })

            // === UPLOAD AUDIO ===
            .addCase(uploadAudioRequest, (state) => {
                state.isUploadingAudio = true;
                state.uploadError = null;
            })
            .addCase(uploadAudioSuccess, (state, action) => {
                state.isUploadingAudio = false;
                state.currentAttempt = action.payload;
                // Add attempt to current session if exists
                if (state.currentSession) {
                    state.currentSession.attempts = state.currentSession.attempts || [];
                    const existingIndex = state.currentSession.attempts.findIndex((a) => a.id === action.payload.id);
                    if (existingIndex === -1) {
                        state.currentSession.attempts.push(action.payload);
                    } else {
                        state.currentSession.attempts[existingIndex] = action.payload;
                    }
                    // Update current attempt number
                    state.currentSession.current_attempt = action.payload.attempt_number + 1;
                }
                // Clear audio blob after successful upload
                state.audioBlob = null;
            })
            .addCase(uploadAudioFailure, (state, action) => {
                state.isUploadingAudio = false;
                state.uploadError = action.payload;
            })

            // === MANUAL STATE UPDATES ===
            .addCase(setCurrentSession, (state, action) => {
                state.currentSession = action.payload;
            });
    },
});

export const { setRecordingState, setAudioBlob, incrementRecordingDuration, clearUploadError, resetState } = sessionsSlice.actions;

export default sessionsSlice.reducer;
